import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ProfileInformationTab = ({ profileData, onProfileDataChange, onSave }) => {
  const [formData, setFormData] = useState({
    displayName: profileData?.displayName || '',
    age: profileData?.age || '',
    city: profileData?.city || '',
    relationshipStatus: profileData?.relationshipStatus || '',
    bio: profileData?.bio || '',
    interests: profileData?.interests || []
  });

  // Sync form data when profileData prop changes
  useEffect(() => {
    if (profileData) {
      setFormData({
        displayName: profileData?.displayName || '',
        age: profileData?.age || '',
        city: profileData?.city || '',
        relationshipStatus: profileData?.relationshipStatus || 'single',
        bio: profileData?.bio || '',
        interests: Array.isArray(profileData?.interests) ? profileData?.interests : []
      });
    }
  }, [profileData]);

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const relationshipOptions = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'separated', label: 'Separated' },
    { value: 'widowed', label: 'Widowed' },
    { value: 'complicated', label: 'It\'s Complicated' }
  ];

  const interestOptions = [
    { value: 'travel', label: 'Travel' },
    { value: 'music', label: 'Music' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'cooking', label: 'Cooking' },
    { value: 'reading', label: 'Reading' },
    { value: 'movies', label: 'Movies' },
    { value: 'sports', label: 'Sports' },
    { value: 'art', label: 'Art' },
    { value: 'photography', label: 'Photography' },
    { value: 'gaming', label: 'Gaming' }
  ];

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    // Update parent component's profileData state
    if (onProfileDataChange) {
      onProfileDataChange(updatedData);
    }
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.displayName?.trim()) {
      newErrors.displayName = 'Display name is required';
    } else if (formData?.displayName?.length < 3) {
      newErrors.displayName = 'Display name must be at least 3 characters';
    }

    if (!formData?.age) {
      newErrors.age = 'Age is required';
    } else if (formData?.age < 18) {
      newErrors.age = 'You must be at least 18 years old';
    } else if (formData?.age > 100) {
      newErrors.age = 'Please enter a valid age';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.relationshipStatus) {
      newErrors.relationshipStatus = 'Please select your relationship status';
    }

    if (formData?.bio?.length > 500) {
      newErrors.bio = 'Bio must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Update parent state with form data
    if (onProfileDataChange) {
      onProfileDataChange(formData);
    }

    // Call parent's save function
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
      } catch (error) {
        console.error('Save error:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleReset = () => {
    setFormData({
      displayName: profileData?.displayName || '',
      age: profileData?.age || '',
      city: profileData?.city || '',
      relationshipStatus: profileData?.relationshipStatus || '',
      bio: profileData?.bio || '',
      interests: profileData?.interests || []
    });
    setErrors({});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Profile Information</h2>
          <p className="text-sm text-muted-foreground mt-1">Update your profile details and preferences</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => setShowPreview(true)}
          className="hidden md:flex"
        >
          Preview
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Display Name"
          type="text"
          placeholder="Enter your display name"
          value={formData?.displayName}
          onChange={(e) => handleInputChange('displayName', e?.target?.value)}
          error={errors?.displayName}
          required
          description="This is how others will see you"
        />

        <Input
          label="Age"
          type="number"
          placeholder="Enter your age"
          value={formData?.age}
          onChange={(e) => handleInputChange('age', e?.target?.value)}
          error={errors?.age}
          required
          min={18}
          max={100}
        />

        <Input
          label="City"
          type="text"
          placeholder="Enter your city"
          value={formData?.city}
          onChange={(e) => handleInputChange('city', e?.target?.value)}
          error={errors?.city}
          required
          description="Only city name will be visible"
        />

        <Select
          label="Relationship Status"
          placeholder="Select status"
          options={relationshipOptions}
          value={formData?.relationshipStatus}
          onChange={(value) => handleInputChange('relationshipStatus', value)}
          error={errors?.relationshipStatus}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Bio <span className="text-muted-foreground">({formData?.bio?.length}/500)</span>
        </label>
        <textarea
          className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
          rows={4}
          placeholder="Tell others about yourself..."
          value={formData?.bio}
          onChange={(e) => handleInputChange('bio', e?.target?.value)}
          maxLength={500}
        />
        {errors?.bio && (
          <p className="text-xs text-error mt-1">{errors?.bio}</p>
        )}
      </div>
      <Select
        label="Interests"
        placeholder="Select your interests"
        options={interestOptions}
        value={formData?.interests}
        onChange={(value) => handleInputChange('interests', value)}
        multiple
        searchable
        clearable
        description="Select up to 5 interests"
      />
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          variant="default"
          fullWidth
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
        >
          Save Changes
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="RotateCcw"
          iconPosition="left"
          onClick={handleReset}
          disabled={isSaving}
        >
          Reset
        </Button>
        <Button
          variant="ghost"
          fullWidth
          iconName="Eye"
          iconPosition="left"
          onClick={() => setShowPreview(true)}
          className="md:hidden"
        >
          Preview Profile
        </Button>
      </div>
      {showPreview && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-md bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold">Profile Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <span className="text-2xl font-heading font-bold text-white">
                    {formData?.displayName?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-lg font-heading font-semibold">{formData?.displayName}</h4>
                  <p className="text-sm text-muted-foreground">{formData?.age} • {formData?.city}</p>
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Relationship Status</p>
                <p className="text-sm font-medium capitalize">
                  {relationshipOptions?.find(opt => opt?.value === formData?.relationshipStatus)?.label}
                </p>
              </div>
              {formData?.bio && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">About</p>
                  <p className="text-sm">{formData?.bio}</p>
                </div>
              )}
              {formData?.interests?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Interests</p>
                  <div className="flex flex-wrap gap-2">
                    {formData?.interests?.map((interest) => (
                      <span
                        key={interest}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                      >
                        {interestOptions?.find(opt => opt?.value === interest)?.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileInformationTab;